import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { env } from '../../environments/environment';
import {
  Inspection,
  InspectionItem,
  InspectionVersion,
  InvalidQuestion,
  ListFilters,
  ListResults,
} from '../shared/interfaces';

@Injectable({
  providedIn: 'root',
})
export class VersionService {
  treeCache: { [key: number]: Promise<InspectionVersion> } = {};
  versionCache: { [key: number]: Promise<InspectionVersion> } = {};
  itemsCache: { [key: number]: Promise<InspectionItem[]> } = {};
  active = true;

  constructor(protected http: HttpClient) {}

  public get(versionId: number) {
    if (!this.versionCache[versionId]) {
      this.versionCache[versionId] = this.http
        .get<InspectionVersion>(`${env.APIUrl}/inspectionVersion/${versionId}`)
        .toPromise();
    }
    return this.versionCache[versionId];
  }

  public items(versionId: number) {
    if (!this.itemsCache[versionId]) {
      this.itemsCache[versionId] = this.http
        .get<InspectionItem[]>(`${env.APIUrl}/inspectionVersion/${versionId}/items`)
        .toPromise();
    }
    return this.itemsCache[versionId];
  }

  public allItemNames() {
    return this.http.get<string[]>(`${env.APIUrl}/inspectionItem/all`).toPromise();
  }

  public latest(versionName: string) {
    return this.http.get<any>(`${env.APIUrl}/inspectionVersion/latest`, { params: { versionName } }).toPromise();
  }

  public async list(name = '', tag = '') {
    return (
      await this.http
        .get<
          ListResults<any>
        >(`${env.APIUrl}/inspectionVersion`, { params: { active: this.active, name, tag, page: 1, pageSize: 99999 } })
        .toPromise()
    )[0];
  }

  public listWithCount(name = '', tag = '', filters?: ListFilters) {
    return this.http
      .get<
        ListResults<any>
      >(`${env.APIUrl}/inspectionVersion`, { params: { active: this.active, name, tag, ...filters } })
      .toPromise();
  }

  public async archived() {
    return (await this.http.get<ListResults<any>>(`${env.APIUrl}/inspectionVersion/archived`).toPromise())[0];
  }

  public archivedWithCount(filters?: ListFilters) {
    return this.http
      .get<ListResults<any>>(`${env.APIUrl}/inspectionVersion/archived`, { params: { ...filters } })
      .toPromise();
  }

  public clone(id: number, versionName: string, checkedSections: number[], isCustom = false) {
    return this.http
      .post<InspectionVersion>(
        `${env.APIUrl}/inspectionVersion/clone/${id}`,
        { checkedSections, isCustom },
        { params: { versionName } },
      )
      .toPromise();
  }

  public checkInspectionVersion(id: number, sectionIds: number[]) {
    return this.http
      .get<{
        isValid: boolean;
        invalidQuestionsCount: number;
        invalidSectionIds: number[];
      }>(`${env.APIUrl}/inspectionVersion/${id}/checkInspectionVersion`, {
        params: { sectionIds: sectionIds.join(',') },
      })
      .toPromise();
  }

  public checkInspectionVersionDeleted(
    id: number,
    deletedItemIds: number[],
    deletedSectionIds: number[],
    deletedQuestionIds: number[] = [],
  ) {
    return this.http
      .get<{
        isValid: boolean;
      }>(`${env.APIUrl}/inspectionVersion/${id}/checkInspectionVersionDeleted`, {
        params: {
          deletedItemIds: deletedItemIds.join(','),
          deletedSectionIds: deletedSectionIds.join(','),
          deletedQuestionIds: deletedQuestionIds.join(','),
        },
      })
      .toPromise();
  }

  public create() {
    return this.http.post<InspectionVersion>(`${env.APIUrl}/inspectionVersion`, null).toPromise();
  }

  public edit(id: number, version: InspectionVersion) {
    return this.http.put<InspectionVersion>(`${env.APIUrl}/inspectionVersion/${id}`, version).toPromise();
  }

  public tree(id: number) {
    if (!this.treeCache[id]) {
      this.treeCache[id] = this.http.get<InspectionVersion>(`${env.APIUrl}/inspectionVersion/${id}/tree`).toPromise();
    }

    return this.treeCache[id];
  }

  public archive(id: number) {
    return this.http.put<InspectionVersion>(`${env.APIUrl}/inspectionVersion/${id}/archive`, null).toPromise();
  }

  public restore(id: number) {
    return this.http.put<InspectionVersion>(`${env.APIUrl}/inspectionVersion/${id}/restore`, null).toPromise();
  }

  public count() {
    return this.http.get<{ active: boolean; count: number }[]>(`${env.APIUrl}/inspectionVersion/count`).toPromise();
  }

  public hasInspections(versionName: string) {
    return this.http
      .get<boolean>(`${env.APIUrl}/inspectionVersion/hasInspections`, { params: { versionName } })
      .toPromise();
  }

  public inspections(versionName: string) {
    return this.http.get<Inspection[]>(`${env.APIUrl}/inspectionVersion/inspections?versionName=${versionName}`);
  }

  public checkTree(id: number) {
    return this.http.get<InvalidQuestion[]>(`${env.APIUrl}/inspectionVersion/${id}/checkTree`).toPromise();
  }

  quantityCanBeZero(itemId: number, versionName: string) {
    return this.http
      .get<boolean>(`${env.APIUrl}/inspectionVersion/quantityCanBeZero/${itemId}`, { params: { versionName } })
      .toPromise();
  }
}
